/**
 * PaymentSuccessPage
 *
 * Stripe redirects here (success_url) after a completed Checkout payment:
 * /invoice/payment-success/:token?session_id=...  Shows a confirmation and
 * re-fetches the invoice to reflect the Paid status (the webhook that flips
 * the status may lag a second or two, so we poll briefly).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import invoiceService from '../../services/invoiceService';
import './PublicInvoicePayment.css';

const MAX_POLLS = 5;

const PaymentSuccessPage = () => {
  const { token } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [confirming, setConfirming] = useState(true);
  const pollsRef = useRef(0);

  const poll = useCallback(async () => {
    try {
      const res = await invoiceService.getPublicInvoice(token);
      const data = res.success ? res.data?.data : null;
      if (data) {
        setInvoice(data);
        if (data.is_paid) {
          setConfirming(false);
          return;
        }
      }
      pollsRef.current += 1;
      if (pollsRef.current >= MAX_POLLS) {
        setConfirming(false);
        return;
      }
      setTimeout(poll, 2000);
    } catch (err) {
      // Stop polling on error to prevent infinite error loops
      setConfirming(false);
    }
  }, [token]);

  useEffect(() => {
    poll();
  }, [poll]);

  return (
    <div className="pip-container">
      <div className="pip-card pip-center">
        <div className="pip-check">✓</div>
        <h1 className="pip-title">Thank you — your payment was received</h1>
        {invoice?.invoice_number ? (
          <p className="pip-muted">Invoice {invoice.invoice_number}</p>
        ) : null}

        {confirming ? (
          <p className="pip-muted">Confirming your payment with our records…</p>
        ) : invoice?.is_paid ? (
          <div className="pip-banner pip-banner-success">
            This invoice is now marked as <strong>Paid</strong>. A receipt has been sent by Stripe.
          </div>
        ) : (
          <div className="pip-banner pip-banner-warn">
            Your payment was successful. It may take a moment for the invoice status to update —
            if it still shows unpaid shortly, please contact us and we will confirm.
          </div>
        )}

        <Link to={`/invoice/pay/${token}`} className="pip-link">View invoice</Link>
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
